import { useActiveElement, useEventListener, useMagicKeys } from "@vueuse/core";
import { computed, toValue, watch, watchEffect, type MaybeRefOrGetter } from "vue";

// type NoSpaces<S extends string> = S extends `${string} ${string}` ? never : S;
//
// type ValidateKey<T extends string> = T extends `${infer Head}+${infer Tail}`
// 	? NoSpaces<Head> extends never
// 		? never
// 		: ValidateKey<Tail>
// 	: NoSpaces<T> extends never
// 		? never
// 		: true;

// export type Key<T extends string> = ValidateKey<T> extends never ? never : T;
export type Key<T extends string> = T;

export type Keys<T extends string[]> = [
	...{
		[K in keyof T]: Key<T[K]>;
	},
];

export type KeysOptions = {
	prevent?: boolean;
	disableOnInput?: boolean;
};

export function useKeys<T extends string[]>(
	commands: MaybeRefOrGetter<Keys<T>>,
	fn: (key: T[number]) => void,
	options: MaybeRefOrGetter<KeysOptions> = {}
) {
	const keysPressed = useMagicKeys();
	const activeElement = useActiveElement();

	const onInput = computed(() => {
		if (!toValue(options).disableOnInput) {
			return false;
		}

		const tag = activeElement.value?.tagName || "";
		return tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON";
	});

	let preventNextEvent = false;

	const keysActive = computed(() => {
		if (onInput.value) {
			return null;
		}

		for (const command of toValue(commands)) {
			if (keysPressed[command].value) {
				return command;
			}
		}

		return null;
	});

	watchEffect(
		() => {
			if (keysActive.value != null && toValue(options).prevent) {
				preventNextEvent = true;
			}
		},
		{ flush: "sync" }
	);

	useEventListener("keydown", (e) => {
		if (preventNextEvent) {
			e.preventDefault();
		}

		preventNextEvent = false;
	});

	watch(keysActive, (key) => {
		if (key == null) {
			return;
		}

		fn(key);
	});
}
